import { FormViewModalProps } from '@shared/components/ViewSelect/modals/FormViewModal';
import { useDispatch } from 'react-redux';
import useViewSelectUpsertConfig, {
  VIEW_SELECT_ACTION_MODE,
  ViewSelectUpsertActionFn,
  ViewSelectUpsertFormData
} from '@shared/components/ViewSelect/hooks/useViewSelectUpsertConfig';
import { getNewNameCount } from '@shared';
import useExpTemplateList from '@experiment-details/hooks/template/useExpTemplateList';
import useSetDefaultExpDetailsTemplate from '@experiment-details/hooks/template/useSetDefaultExpDetailsTemplate';
import useSaveExpDetailsTemplateMutation from '@experiment-details/hooks/template/useSaveExpDetailsTemplateMutation';
import useDuplicateExpDetailsTemplateMutation from '@experiment-details/hooks/template/useDuplicateExpDetailsTemplateMutation';
import useSelectExpDetailsTemplate from '@experiment-details/hooks/template/useSelectExpDetailsTemplate';
import alertsUtil from '@/util/alertsUtil';
import { snackbarTypes } from '@/constants/alertTypes';
import { EMPTY_EXP_DETAILS_TEMPLATE } from '@experiment-management-shared/constants/emptyPersonalTemplate';
import useExpDetailsPersonalTemplate from '@experiment-details/hooks/template/useExpDetailsPersonalTemplate';
import { ExperimentTemplateData } from '@experiment-details/types';
import { cloneDeep } from 'lodash';
import { AUTO_GENERATED_OPTION_ID } from '@shared/constants';
import { ViewItemData } from '@shared/components/ViewSelect/utils';

const useExpDetailsViewUpsert = () => {
  const dispatch = useDispatch();

  const { templateData: personalTemplate } = useExpDetailsPersonalTemplate();

  const setDefaultTemplateMutation = useSetDefaultExpDetailsTemplate();
  const saveTemplateMutation = useSaveExpDetailsTemplateMutation();
  const duplicateTemplateMutation = useDuplicateExpDetailsTemplateMutation();

  const selectTemplate = useSelectExpDetailsTemplate();

  const {
    newViewDefaultName,
    modalOpened,
    editId,
    mode,
    modalCopy,
    onEdit,
    onCreate,
    onDuplicate,
    onClose
  } = useViewSelectUpsertConfig();

  const { data: templates = [] } = useExpTemplateList();
  const viewData = templates.find(view => view.templateId === editId);
  const templateNames = templates.map(template => template.templateName || '');

  const isPersonalView =
    editId === AUTO_GENERATED_OPTION_ID || viewData?.personalView;

  const clonedViewDefaultName = isPersonalView
    ? newViewDefaultName
    : `${viewData?.templateName}`;

  const clonesCount = getNewNameCount(
    templateNames,
    `${clonedViewDefaultName} (Copy`
  );
  const notRenamedNewViewCount = getNewNameCount(
    templateNames,
    `${newViewDefaultName}`,
    /\((\d+)\)$/
  );

  const onEditTemplate = (id: string) => {
    onEdit(id);
  };

  const onCreateNewTemplate = () => {
    onCreate();
  };

  const onDuplicateTemplate = (id: string) => {
    onDuplicate(id);
  };

  const onEditView: ViewSelectUpsertActionFn = (templateName, isDefault) => {
    const templateId = viewData?.templateId;
    if (!templateId) return;

    saveTemplateMutation.mutate(
      {
        templateId,
        formData: { name: templateName, isDefault }
      },
      {
        onSuccess: () => {
          if (isDefault) {
            selectTemplate(templateId);
          }

          onClose();
        }
      }
    );
  };

  const onDuplicateView: ViewSelectUpsertActionFn = (
    templateName,
    isDefault
  ) => {
    const templateId = viewData?.templateId;

    const onSuccess = (templateData: ExperimentTemplateData) => {
      selectTemplate(templateData.templateId);

      onClose();
      dispatch(
        alertsUtil.openSnackbarDialog(
          snackbarTypes.SUCCESS_API_RESPONSE,
          `Your view was successfully duplicated`
        )
      );
    };
    if (!templateId || viewData.personalView) {
      if (!personalTemplate) return;

      const personalTemplateData: ExperimentTemplateData = cloneDeep(
        personalTemplate
      );

      personalTemplateData.chartTemplate.autoGeneratedDraft = false;
      personalTemplateData.chartTemplate.panels.autoGenerate = false;

      // if user duplicates unsaved personal template
      duplicateTemplateMutation.mutate(
        {
          templateData: personalTemplateData,
          formData: {
            name: templateName,
            isDefault
          },
          personalTemplateId: personalTemplateData.templateId
        },
        { onSuccess }
      );
      return;
    }

    duplicateTemplateMutation.mutate(
      {
        templateId,
        formData: { name: templateName, isDefault }
      },
      {
        onSuccess
      }
    );
  };

  const onCreateNewView: ViewSelectUpsertActionFn = (
    templateName,
    isDefault
  ) => {
    const template: ExperimentTemplateData = {
      ...EMPTY_EXP_DETAILS_TEMPLATE,
      templateName
    };
    template.chartTemplate.autoGeneratedDraft = false;
    template.chartTemplate.panels.autoGenerate = false;

    duplicateTemplateMutation.mutate(
      {
        templateData: template,
        formData: {
          name: templateName,
          isDefault
        }
      },
      {
        onSuccess: template => {
          selectTemplate(template.templateId);

          onClose();
          dispatch(
            alertsUtil.openSnackbarDialog(
              snackbarTypes.SUCCESS_API_RESPONSE,
              `Your view was successfully created`
            )
          );
        }
      }
    );
  };

  const onSelectView = (templateId: string, option: ViewItemData) => {
    selectTemplate(
      option.isAutogenerated ? AUTO_GENERATED_OPTION_ID : templateId
    );
  };

  const onSetAsDefaultView = (templateName: string, templateId?: string) => {
    setDefaultTemplateMutation.mutate(templateId, {
      onSuccess: () => {
        dispatch(
          alertsUtil.openSnackbarDialog(
            snackbarTypes.SUCCESS_API_RESPONSE,
            `The default view for all users in this project was changed to "${templateName}".`
          )
        );
        selectTemplate(templateId);
      }
    });
  };

  const confirmActionsMap: Record<
    VIEW_SELECT_ACTION_MODE,
    ViewSelectUpsertActionFn
  > = {
    [VIEW_SELECT_ACTION_MODE.EDIT_MODE]: onEditView,
    [VIEW_SELECT_ACTION_MODE.DUPLICATE_MODE]: onDuplicateView,
    [VIEW_SELECT_ACTION_MODE.NEW_MODE]: onCreateNewView
  };

  const nameMap: Record<VIEW_SELECT_ACTION_MODE, string> = {
    [VIEW_SELECT_ACTION_MODE.DUPLICATE_MODE]: `${clonedViewDefaultName} ${
      clonesCount ? `(Copy ${clonesCount})` : '(Copy)'
    }`,
    [VIEW_SELECT_ACTION_MODE.EDIT_MODE]: viewData?.templateName || '',
    [VIEW_SELECT_ACTION_MODE.NEW_MODE]: `${newViewDefaultName}${
      notRenamedNewViewCount ? ` (${notRenamedNewViewCount})` : ''
    }`
  };

  const formDataMap: Record<
    VIEW_SELECT_ACTION_MODE,
    ViewSelectUpsertFormData
  > = {
    [VIEW_SELECT_ACTION_MODE.DUPLICATE_MODE]: {
      name: nameMap[mode],
      isDefault: false
    },
    [VIEW_SELECT_ACTION_MODE.EDIT_MODE]: {
      name: nameMap[mode],
      isDefault: Boolean(viewData?.defaultTemplate)
    },
    [VIEW_SELECT_ACTION_MODE.NEW_MODE]: {
      name: nameMap[mode],
      isDefault: false
    }
  };

  const isEditMode = mode === VIEW_SELECT_ACTION_MODE.EDIT_MODE;

  let existedNames = templateNames;

  if (isEditMode) {
    existedNames = existedNames.filter(name => name !== viewData?.templateName);
  }
  if (!modalOpened) {
    existedNames = [];
  }

  const actionModalConfig: FormViewModalProps = {
    onConfirm: confirmActionsMap[mode],
    onClose,
    isOpened: modalOpened,
    isLoading:
      duplicateTemplateMutation.isLoading ||
      saveTemplateMutation.isLoading ||
      setDefaultTemplateMutation.isLoading,
    title: modalCopy.title,
    primaryButtonText: modalCopy.primaryButtonText,
    formData: formDataMap[mode],
    isEditMode,
    existedNames
  };

  return {
    onSetAsDefaultView,
    onSelectView,
    onEditTemplate,
    onDuplicateTemplate,
    onCreateNewTemplate,
    actionModalConfig
  };
};

export default useExpDetailsViewUpsert;
